import {
	type QuestionnaireAnswers,
	type QuestionnaireContent,
	type StepperRef,
	useQuestionnaire
} from '@capsicum/common';
import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { Text } from '../../atoms/text/Text';
import { RadioButtonInput } from '../form';
import { Stepper } from '../stepper/Stepper';

type QuestionnaireProps = {
	content: QuestionnaireContent;
	customBackNavigation?: () => void;
	isLoading?: boolean;
	onlyButtonsNavigation?: boolean;
	submit: (answers: QuestionnaireAnswers) => void;
	testID: string;
};

export const Questionnaire = ({
	content,
	customBackNavigation,
	isLoading = false,
	onlyButtonsNavigation,
	submit,
	testID
}: QuestionnaireProps) => {
	const [currentStep, setCurrentStep] = useState(0);
	const stepperRef = useRef<StepperRef>(null);
	const { questionnaireAnswers, updateAnswers } = useQuestionnaire({ content });

	const steps = useMemo(
		() =>
			content.map((step, stepIndex) => {
				const Question = () => (
					<S.StepContainer>
						<S.Question variant="title6">{step.question.text}</S.Question>
						<S.ContentContainer>
							<S.RadioButtonsContainer>
								{step.answers.map((answer, answerIndex) => (
									<S.AnswerWrapper
										$selected={answer.id === questionnaireAnswers[stepIndex]?.answerId}
										key={answer.id}
										onClick={() => updateAnswers(stepIndex, answer)}
									>
										<S.Answer
											checked={answer.id === questionnaireAnswers[stepIndex]?.answerId}
											item={{
												id: answer.id,
												label: answer.text
											}}
											pressableLabels
											testID={`questionnaire-${testID}-question-${stepIndex}-answer-${answerIndex}`}
											onPress={() => updateAnswers(stepIndex, answer)}
										/>
									</S.AnswerWrapper>
								))}
							</S.RadioButtonsContainer>
						</S.ContentContainer>
					</S.StepContainer>
				);

				return Question;
			}),
		[questionnaireAnswers]
	);

	return (
		<S.StepperWrapper data-testid={`questionnaire-${testID}`}>
			<Stepper
				customBackNavigation={customBackNavigation}
				disabledFirstButton={!questionnaireAnswers[currentStep]?.answerId}
				isLoading={isLoading}
				onlyButtonsNavigation={onlyButtonsNavigation}
				ref={stepperRef}
				showStepsNavigation
				steps={steps}
				testID={`questionnaire-${testID}-stepper`}
				onStepIndexChange={index => setCurrentStep(index)}
				onSubmitSteps={() => submit(questionnaireAnswers)}
			/>
		</S.StepperWrapper>
	);
};

namespace S {
	export const StepperWrapper = styled.section`
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			margin: ${({ theme }) => theme.spacings.small};
		}
	`;

	export const StepContainer = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const Answer = styled(RadioButtonInput)`
		width: 100%;
	`;

	export const Question = styled(Text)`
		${({ theme }) => theme.fonts.headings.title6};
		margin-bottom: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const AnswerWrapper = styled.div<{ $selected: boolean }>`
		padding: ${({ theme }) => `${theme.spacings.small} ${theme.spacings.medium}`};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.screen};
		${({ theme }) => theme.border.radius.medium};
		cursor: pointer;
		${({ $selected, theme }) =>
			`border: 1px solid ${
				$selected ? theme.colors.borders.brand.enabled : theme.colors.backgrounds.main.screen
			};`}
	`;

	export const ContentContainer = styled.div`
		${({ theme }) => theme.border.radius.medium};
	`;

	export const RadioButtonsContainer = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xsmall};
	`;
}
